import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsIntranet';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './CorreoPlantillaModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import ConfirmDialog from '../../../../Lms/Components/Shared/ConfirmDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Correos Plantilla (Intranet)";
const endpoint = ENDPOINTS.correoPlantilla;
const endpointById = ENDPOINTS.correoPlantillaById;
const modelo = Models.correoPlantilla;

const maps = {
    mapCreate: (f) => ({
        idCorreoPlantilla: 0,
        asuntoCorreo: f.asuntoCorreo,
        contenidoCorreo: f.contenidoCorreo,
        momento: f.momento,
        enviar: f.enviar,
        activo: true, // Por defecto activo en Intranet
        programarEnvio: f.programarEnvio,
        diaEnvio: f.diaEnvio ? parseInt(f.diaEnvio) : null,
        horaEnvio: f.horaEnvio || null
    }),
    mapUpdate: (id, f) => ({
        idCorreoPlantilla: id || 0,
        asuntoCorreo: f.asuntoCorreo,
        contenidoCorreo: f.contenidoCorreo,
        momento: f.momento,
        enviar: f.enviar,
        activo: f.activo,
        programarEnvio: f.programarEnvio,
        diaEnvio: f.diaEnvio ? parseInt(f.diaEnvio) : null,
        horaEnvio: f.horaEnvio || null
    }),
};

export default function CorreoPlantillaCatalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

    // Modales de Alerta/Confirmación
    const [alertConfig, setAlertConfig] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmConfig, setConfirmConfig] = useState({ open: false, title: '', message: '', onConfirm: () => { } });

    const editorRef = useRef(null);

    // Cargar datos
    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);
        } catch (error) {
            console.error('Error al cargar plantillas intranet:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    // Filtrado
    const filtered = useMemo(() => {
        let data = rows;
        if (query.trim()) {
            const q = normalizeValue(query);
            data = data.filter((e) => {
                const asunto = normalizeValue(e.asuntoCorreo);
                const contenido = normalizeValue(e.contenidoCorreo);
                return (
                    asunto.includes(q) ||
                    contenido.includes(q)
                );
            });
        }
        return data;
    }, [rows, query]);

    // Paginación
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    // Nuevo
    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    // Editar
    const handleEdit = (id) => {
        const r = rows.find((x) => x.idCorreoPlantilla === id);
        if (!r) return;
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
    };

    // Borrar
    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            setAlertConfig({
                open: true,
                title: 'Error',
                message: 'No se pudo eliminar el registro.',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Enviar Correo Individual (Prueba)
    const handleSendTest = async (id) => {
        const dest = window.prompt("Ingrese el correo de destino para la prueba:");
        if (!dest) return;

        try {
            setLoading(true);
            await axios.post(ENDPOINTS.enviarCorreoPlantilla(id), { destinatario: dest });
            setAlertConfig({
                open: true,
                title: 'Correo enviado',
                message: 'El correo de prueba ha sido enviado correctamente.',
                variant: 'success'
            });
        } catch (error) {
            console.error('Error al enviar correo de prueba:', error);
            setAlertConfig({
                open: true,
                title: 'Error',
                message: 'Ocurrió un error al intentar enviar el correo de prueba.',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Enviar Correo Masivo (A todos los Usuarios con Email)
    const handleSendMassive = async (id, asunto) => {
        setConfirmConfig({
            open: true,
            title: 'Confirmar envío masivo (Intranet)',
            message: `¿Estás seguro de enviar la plantilla "${asunto}" a TODOS los usuarios del sistema?`,
            onConfirm: async () => {
                try {
                    setLoading(true);
                    const res = await axios.post(ENDPOINTS.enviarMasivoCorreoPlantilla(id));
                    setAlertConfig({
                        open: true,
                        title: 'Proceso completado',
                        message: (
                            <div>
                                <p>Total destinatarios: <strong>{res.data.total}</strong></p>
                                <p>Enviados con éxito: <strong>{res.data.enviados}</strong></p>
                            </div>
                        ),
                        variant: 'success'
                    });
                } catch (error) {
                    console.error('Error al enviar masivo:', error);
                    setAlertConfig({
                        open: true,
                        title: 'Error de envío',
                        message: 'Ocurrió un error al intentar enviar los correos masivos.',
                        variant: 'error'
                    });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Guardar
    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                await axios.post(endpoint, maps.mapCreate(form));
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, form));
            }
            setModalOpen(false);
            await loadData();
            setAlertConfig({
                open: true,
                title: 'Transacción exitosa',
                message: 'El registro se ha guardado correctamente.',
                variant: 'success'
            });
        } catch (e) {
            console.error(e);
            setAlertConfig({
                open: true,
                title: 'Error al guardar',
                message: 'Ocurrió un problema al intentar guardar los cambios.',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEnviar = async (item) => {
        const nuevoEnviar = !item.enviar;
        const payload = maps.mapUpdate(item.idCorreoPlantilla, {
            ...item,
            enviar: nuevoEnviar
        });

        try {
            setLoading(true);
            await axios.put(endpoint, payload);
            await loadData();
        } catch (e) {
            console.error('Error al cambiar estado de envío (Intranet):', e);
            setAlertConfig({
                open: true,
                title: 'Error',
                message: 'No se pudo cambiar el estado de envío.',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="panel-sigma-main">
                <div className="roles-shell">
                    <div className="roles-topbar">
                        <h2 className="roles-title">{nombreTabla}</h2>

                        <input
                            className="roles-search"
                            placeholder="Buscar..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(1);
                            }}
                        />

                        <button
                            type="button"
                            className="btn-action"
                            onClick={handleNew}
                        >
                            ＋ Nuevo
                        </button>
                    </div>

                    {loading ? (
                        <div className="catalog-loading">Cargando...</div>
                    ) : (
                        <div className="roles-table-wrap">
                            <div className="roles-table-scroller">
                                <table className="roles-table">
                                    <thead>
                                        <tr>
                                            <th>Asunto</th>
                                            <th>Momento</th>
                                            <th style={{ textAlign: 'center' }}>Habilitado</th>
                                            <th style={{ textAlign: 'center' }}>Probar</th>
                                            <th style={{ textAlign: 'center' }}>Enviar Masivo</th>
                                            <th style={{ textAlign: 'center' }}>Editar</th>
                                            <th style={{ textAlign: 'center' }}>Borrar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center' }}>
                                                    No se encontraron registros.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((e) => (
                                                <tr key={e.idCorreoPlantilla}>
                                                    <td>{e.asuntoCorreo}</td>
                                                    <td>{e.momento}</td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                        <div
                                                            onClick={() => !loading && handleToggleEnviar(e)}
                                                            style={{
                                                                width: '38px',
                                                                height: '20px',
                                                                backgroundColor: e.enviar ? '#10b981' : '#cbd5e1',
                                                                borderRadius: '12px',
                                                                position: 'relative',
                                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                                transition: 'background-color 0.2s',
                                                                flexShrink: 0,
                                                                opacity: loading ? 0.6 : 1,
                                                                margin: '0 auto'
                                                            }}
                                                            title={e.enviar ? "Desactivar envío" : "Activar envío"}
                                                        >
                                                            <div style={{
                                                                width: '14px',
                                                                height: '14px',
                                                                backgroundColor: 'white',
                                                                borderRadius: '50%',
                                                                position: 'absolute',
                                                                top: '3px',
                                                                left: e.enviar ? '21px' : '3px',
                                                                transition: 'left 0.2s',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                            }} />
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="roles-btn"
                                                            style={{ padding: '2px 8px', fontSize: '0.75rem', minWidth: 'auto' }}
                                                            onClick={() => handleSendTest(e.idCorreoPlantilla)}
                                                            title="Enviar correo de prueba"
                                                        >
                                                            Probar
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="btn-action"
                                                            style={{ padding: '2px 8px', fontSize: '0.75rem', minWidth: 'auto', backgroundColor: '#28a745' }}
                                                            onClick={() => handleSendMassive(e.idCorreoPlantilla, e.asuntoCorreo)}
                                                            title="Enviar a todos los usuarios"
                                                        >
                                                            Masivo
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="rk-iconbtn rk-iconbtn--edit"
                                                            onClick={() => handleEdit(e.idCorreoPlantilla)}
                                                            title="Editar"
                                                        >
                                                            <img src={sigmaEdit} alt="" className="rk-ico" />
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="rk-iconbtn rk-iconbtn--del"
                                                            onClick={() => askDelete(e.idCorreoPlantilla, e.asuntoCorreo)}
                                                            title="Borrar"
                                                        >
                                                            <img src={sigmaDelete} alt="" className="rk-ico" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="roles-pagination">
                        <div className="roles-pagination-left">
                            {filtered.length > 0
                                ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                                : <span>0 resultados</span>}
                        </div>
                        <div className="roles-pagination-right">
                            <PageBtn
                                label="‹"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                            />
                            {pageNumbers.map((n) => (
                                <PageBtn
                                    key={n}
                                    label={n}
                                    onClick={() => setPage(n)}
                                    active={n === page}
                                />
                            ))}
                            <PageBtn
                                label="›"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            />
                        </div>
                    </div>

                    {modalOpen && (
                        <div className="roles-editor-slot" ref={editorRef}>
                            <Modal
                                open={modalOpen}
                                mode={modalMode}
                                data={editData}
                                onClose={() => setModalOpen(false)}
                                onSubmit={handleSubmit}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />

            <ConfirmDialog
                open={confirmConfig.open}
                title={confirmConfig.title}
                message={confirmConfig.message}
                onConfirm={confirmConfig.onConfirm}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
            />
        </>
    );
}
